// Prawo miejscowe — kontrakty modułu aktów prawa miejscowego.
//
// Port Kwantum.Documents.LegalAct / LegalActMetric oraz list LocalLaw* z Magistratu.
// Treść aktu (docx) jest zwykłym dokumentem edytora z Type = LegalAct — otwiera ją
// wtyczka Worda przez ItemKind.EditorDocument i pole DocumentId poniżej.
//
// UWAGA: pliki aktu (docx, XML, ZIPX, załączniki) są JAWNE — akt prawa miejscowego
// jest dokumentem publicznym, a jego XML jest podstawą publikacji.

/** Skrzynki modułu — odpowiadają pozycjom menu „Prawo miejscowe" z Magistratu. */
export enum ELegalActBox {
  Projects = 0,
  ToApprove = 1,
  Adopted = 2,
  ToPublish = 3,
  InForce = 4,
  All = 9,
}

/** Skrócony obieg projektu aktu (LegalActWorkflowStatus po stronie BE). */
export enum ELegalActWorkflowStatus {
  Project = 0,
  ToFix = 1,
  Approved = 15,
  ToApprove = 18,
  InForce = 19,
  Adopted = 23,
  ReadyToDispatch = 29,
  Rejected = 32,
}

/** Status metrykalny aktu (Kwantum.Enums.LegalActStatus). */
export enum ELegalActStatus {
  NotSet = 0,
  Projekt = 1,
  Uchwalony = 2,
  Przyjety = 3,
  Ogloszony = 4,
  DoOpracowania = 5,
  WOpracowaniu = 6,
  DoKonsultacji = 8,
  DoUchwaleniaNaNajblizszejSesji = 9,
  Uniewazniony = 91,
  Archiwalny = 99,
}

/** Rodzaje aktu używane wprost w UI; pełna lista przychodzi z /locallaw/act-types. */
export enum ELegalActType {
  Uchwala = 2,
  Zarzadzenie = 3,
  ZarzadzenieWewnetrzne = 22,
}

/** Rodzaj załącznika aktu (LegalActAttachmentKind po stronie BE). */
export enum ELegalActAttachmentKind {
  Text = 0,
  Binary = 1,
  Justification = 2,
  Osr = 3,
  SupervisoryRuling = 4,
}

/** Uprawnienia biura rady — flagi Kwantum.Enums.LocalLawAccess. */
export enum ELocalLawAccess {
  None = 0,
  ManageResolutions = 1,
  ManageOrdinances = 2,
  BasicManagement = 4,
}

export interface ILegalActListItem {
  Id: string;
  /** Dokument edytora z treścią aktu — tym otwiera się akt w Wordzie. */
  DocumentId: string;

  ActType: ELegalActType | number;
  ActTypeName: string;
  ActNumber: string | null;
  Title: string | null;
  Author: string | null;

  ActStatus: ELegalActStatus;
  ActStatusName: string;

  Status: ELegalActWorkflowStatus;
  StatusName: string;

  Date: string | null;
  AdoptedDate: string | null;
  PublicationDate: string | null;
  EffectiveFrom: string | null;

  PublishInBip: boolean;
  HasXml: boolean;
  HasZipx: boolean;
  Repeal: boolean;
  PartialRepeal: boolean;

  CreatedBy: string | null;
  CreatedByUserId: string;
  AssignedTo: string | null;
  AssignedToUserId: string | null;

  CreationDate: string;
  ModificationDate: string | null;

  StatusComment: string | null;
  AttachmentsCount: number;

  CanEdit: boolean;
  CanManage: boolean;
}

export interface ILegalActAttachment {
  Id: string;
  Kind: ELegalActAttachmentKind;
  KindName: string;
  Number: string | null;
  Title: string | null;
  MarkId: string | null;
  FileUri: string | null;
  FileName: string | null;
  ContentType: string | null;
  Size: number | null;
  Order: number;
}

export interface ILegalActLogEntry {
  Id: string;
  Action: string;
  ActionName: string;
  Status: ELegalActWorkflowStatus;
  Who: string | null;
  Target: string | null;
  Comment: string | null;
  When: string;
}

export interface ILegalAct extends ILegalActListItem {
  EffectiveTo: string | null;
  BackDate: string | null;

  IsNormative: boolean;
  IsIndependent: boolean;
  IsArchival: boolean;

  BipTitle: string | null;
  BipDescription: string | null;
  BipPublicationDate: string | null;
  BipCategory: string | null;

  RepealInfo: string | null;
  SupervisoryRulingDate: string | null;
  SupervisoryRulingUri: string | null;
  SupervisoryRulingFileName: string | null;

  XmlUri: string | null;
  XmlGeneratedDate: string | null;
  ZipxUri: string | null;
  ZipxGeneratedDate: string | null;

  JournalName: string | null;
  JournalYear: number | null;
  JournalPosition: number | null;

  FileUri: string | null;
  FileName: string | null;

  Attachments: ILegalActAttachment[];
  Log: ILegalActLogEntry[];
}

export interface ILegalActType {
  Value: number;
  Name: string;
}

export interface ILegalActCoworker {
  UserId: string;
  Name: string;
  Email: string | null;
  Access: number;
  IsProfileOwner: boolean;
  /** To bieżący użytkownik — projekt wolno przekazać do akceptacji także sobie. */
  IsMe: boolean;
}

// ── Żądania ────────────────────────────────────────────────────────────────

export interface ICreateLegalActRequest {
  ActType: number;
  Title: string | null;
  ActNumber: string | null;
  Author: string | null;
  Date: string | null;
  TemplateId: string | null;
  SourceLegalActId?: string | null;
}

export interface ISaveLegalActMetricRequest {
  ActType: number;
  ActNumber: string | null;
  Title: string | null;
  Author: string | null;
  ActStatus: ELegalActStatus;

  Date: string | null;
  AdoptedDate: string | null;
  PublicationDate: string | null;
  EffectiveFrom: string | null;
  EffectiveTo: string | null;
  BackDate: string | null;

  IsNormative: boolean;
  IsIndependent: boolean;
  IsArchival: boolean;

  JournalName: string | null;
  JournalYear: number | null;
  JournalPosition: number | null;
}

export interface ISaveLegalActBipRequest {
  PublishInBip: boolean;
  BipTitle: string | null;
  BipDescription: string | null;
  BipPublicationDate: string | null;
  BipCategory: string | null;
}

export interface ISaveSupervisoryRulingRequest {
  Repeal: boolean;
  PartialRepeal: boolean;
  RepealInfo: string | null;
  SupervisoryRulingDate: string | null;
  FileUri: string | null;
  FileName: string | null;
}

export interface ILegalActWorkflowRequest {
  RecipientUserId?: string | null;
  Comment?: string | null;
  ActNumber?: string | null;
  AdoptedDate?: string | null;
}

export interface ISaveLegalActPermissionRequest {
  UserId: string;
  Access: number;
}

export interface ILegalActFileDownload {
  Bucket: string | null;
  /** Po stronie /url zwraca podpisany odnośnik, nie klucz w repozytorium. */
  Uri: string | null;
  FileName: string | null;
  ContentType: string | null;
  Size: number | null;
}
